import { useEffect, useMemo, useState } from 'react';
import { fetchTranslations } from '../../services/api';
import { updateTranslations, translateText } from '../../services/adminApi';
import { flattenObject, unflattenObject } from '../../utils/flattenObject';
import Button from '../../components/admin/ui/Button';
import Loader from '../../components/admin/ui/Loader';
import Textarea from '../../components/admin/ui/Textarea';
import EntityFormPage from '../../components/admin/EntityFormPage';

function AdminTranslationsPage({ showToast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [translatingKey, setTranslatingKey] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    fetchTranslations()
      .then((data) => {
        const flatFr = flattenObject(data?.fr ?? {});
        const flatEn = flattenObject(data?.en ?? {});
        const keys = Array.from(new Set([...Object.keys(flatFr), ...Object.keys(flatEn)])).sort();
        setRows(keys.map((key) => ({ key, fr: flatFr[key] ?? '', en: flatEn[key] ?? '' })));
      })
      .finally(() => setLoading(false));
  }, []);

  const sections = useMemo(() => {
    const grouped = new Map();
    rows.forEach((row) => {
      const section = row.key.split('.')[0];
      if (!grouped.has(section)) grouped.set(section, []);
      grouped.get(section).push(row);
    });
    return Array.from(grouped.entries());
  }, [rows]);

  const updateRow = (key, lang, value) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [lang]: value } : row)));
  };

  const autoTranslate = async (key) => {
    const row = rows.find((r) => r.key === key);
    if (!row?.fr) return;
    setTranslatingKey(key);
    try {
      const { translatedText } = await translateText(row.fr, 'fr', 'en');
      updateRow(key, 'en', translatedText);
    } catch (error) {
      showToast('Traduction automatique indisponible.');
    } finally {
      setTranslatingKey(null);
    }
  };

  const save = async () => {
    const frFlat = {};
    const enFlat = {};
    rows.forEach((row) => {
      frFlat[row.key] = row.fr;
      enFlat[row.key] = row.en;
    });
    try {
      await updateTranslations('fr', unflattenObject(frFlat));
      await updateTranslations('en', unflattenObject(enFlat));
      showToast('Textes du site mis à jour.');
    } catch (error) {
      showToast("Erreur lors de l'enregistrement des textes.");
    }
  };

  if (loading) return <Loader label="Chargement des textes..." />;

  if (activeSection) {
    const sectionRows = sections.find(([section]) => section === activeSection)?.[1] ?? [];
    return (
      <EntityFormPage
        listLabel="Textes des pages"
        itemLabel={activeSection}
        title={activeSection}
        onBack={() => setActiveSection(null)}
        actions={<Button onClick={save}>Enregistrer</Button>}
      >
        {sectionRows.map((row) => (
          <div className="admin-text-row" key={row.key}>
            <div className="admin-text-key">{row.key}</div>
            <div className="admin-lang-cols">
              <Textarea label="FR" value={row.fr} onChange={(e) => updateRow(row.key, 'fr', e.target.value)} />
              <Textarea label="EN" value={row.en} onChange={(e) => updateRow(row.key, 'en', e.target.value)} />
            </div>
            <Button variant="secondary" onClick={() => autoTranslate(row.key)} disabled={translatingKey === row.key}>
              {translatingKey === row.key ? 'Traduction...' : 'Traduire FR → EN'}
            </Button>
          </div>
        ))}
      </EntityFormPage>
    );
  }

  return (
    <section className="admin-section">
      <div className="admin-actions" style={{ justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <h2>Textes des pages</h2>
        <Button onClick={save}>Enregistrer tous les textes</Button>
      </div>

      <div className="admin-section-grid">
        {sections.map(([section, sectionRows]) => (
          <button type="button" className="admin-section-card" key={section} onClick={() => setActiveSection(section)}>
            <div className="admin-section-card-name">{section}</div>
            <div className="admin-section-card-count">
              {sectionRows.length} texte{sectionRows.length > 1 ? 's' : ''}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

export default AdminTranslationsPage;
